import { useState } from 'react';
import { Alert, Button, Card, Icon, Table, cn, type Column } from '@evogard/ui';
import type { ManagementDurations } from '@evogard/contract';
import { count, type ClaimFlowPeriod } from '@evogard/domain';
import { LINE_LABEL, LINE_TONE } from './managementLabels.js';
import { useManagementDurations } from './useManagement.js';

/**
 * O tempo por departamento (26/10/2026): quantos dias o sinistro fica em
 * Análise, Reparo e Indenização, na coorte do fluxo
 * (`summarizeDepartmentDurations`, em `@evogard/domain`, diz o que conta).
 *
 * A barra cheia é a MÉDIA de quem já saiu, e o traço sobre ela é a MEDIANA —
 * a média sozinha esconde o sinistro que ficou meses e puxa a conta; os dois
 * juntos dizem se o departamento é lento ou se tem uns poucos parados. A
 * barra fina e clara embaixo é há quantos dias estão os que ainda estão lá:
 * fica à parte, porque quem não saiu ainda não tem tempo final.
 *
 * As cores são as das linhas do mapa e do fluxo, e cada número está escrito.
 */

const days1 = new Intl.NumberFormat('pt-BR', { maximumFractionDigits: 1 });
const inDays = (n: number) => `${days1.format(n)} ${n === 1 ? 'dia' : 'dias'}`;

type Row = ManagementDurations['departments'][number];

export function ClaimDurationsChart({ days }: { days: ClaimFlowPeriod }) {
  const [asTable, setAsTable] = useState(false);
  const query = useManagementDurations(days);
  const data = query.data;
  const any = !!data?.departments.some((d) => d.done + d.inProgress > 0);

  return (
    <Card
      as="figure"
      title="Tempo por departamento"
      descricao="Dias nas etapas de trabalho: média e mediana de quem já saiu, e há quanto tempo estão os que ficaram."
      actions={
        any &&
        !query.isError && (
          <Button variant="discreto" size="sm" onClick={() => setAsTable((v) => !v)}>
            <Icon nome={asTable ? 'grafico' : 'grade'} size={16} />
            {asTable ? 'Ver gráfico' : 'Ver tabela'}
          </Button>
        )
      }
    >
      {query.isError ? (
        <Alert tone="erro" title="Não conseguimos carregar o tempo por departamento">
          Nada foi desenhado, para não mostrar número velho como se fosse atual.
          <span className="mt-1 block font-mono text-xs">{query.error.message}</span>
        </Alert>
      ) : !data ? (
        <div
          className="h-48 animate-pulse rounded-borda bg-superficie-suave"
          aria-busy="true"
        />
      ) : !any ? (
        <p className="py-10 text-center text-sm text-texto-suave">
          Nenhum sinistro aberto nos últimos {days} dias passou por um departamento.
          Quando passar, o gráfico mostra quanto tempo ficou em cada um.
        </p>
      ) : asTable ? (
        <DurationsTable data={data} />
      ) : (
        <DurationsFigure data={data} />
      )}
    </Card>
  );
}

function DurationsFigure({ data }: { data: ManagementDurations }) {
  const max = Math.max(
    1,
    ...data.departments.flatMap((d) => [
      d.meanDays ?? 0,
      d.medianDays ?? 0,
      d.inProgressMeanDays ?? 0,
    ]),
  );
  const pct = (n: number) => `${(n / max) * 100}%`;

  return (
    <div className="flex flex-col gap-5">
      <ol className="flex flex-col gap-5" aria-label="Tempo médio por departamento">
        {data.departments.map((d) => (
          <li
            key={d.department}
            className="grid grid-cols-[minmax(0,7rem)_minmax(0,1fr)] items-start gap-x-4 gap-y-1"
          >
            <span className="pt-0.5 text-sm font-semibold text-texto">
              {LINE_LABEL[d.department]}
            </span>
            <div className="flex min-w-0 flex-col gap-1.5">
              <div className="flex items-center gap-3">
                <div className="relative h-4 flex-1 rounded-pilula bg-superficie-suave">
                  {d.meanDays !== null && (
                    <span
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-pilula',
                        LINE_TONE[d.department].fill,
                      )}
                      style={{ width: pct(d.meanDays) }}
                    />
                  )}
                  {d.medianDays !== null && (
                    <span
                      aria-hidden
                      title={`Mediana: ${inDays(d.medianDays)}`}
                      className="absolute -inset-y-1 w-0.5 rounded-pilula bg-texto"
                      style={{ left: `calc(${pct(d.medianDays)} - 1px)` }}
                    />
                  )}
                </div>
                <span className="w-20 shrink-0 text-right text-sm tabular-nums">
                  {d.meanDays === null ? (
                    <span className="text-texto-suave">—</span>
                  ) : (
                    <b className="font-semibold text-texto">{inDays(d.meanDays)}</b>
                  )}
                </span>
              </div>
              {d.inProgressMeanDays !== null && (
                <div className="flex items-center gap-3">
                  <div className="relative h-1.5 flex-1">
                    <span
                      className={cn(
                        'absolute inset-y-0 left-0 rounded-pilula',
                        LINE_TONE[d.department].fill,
                        'opacity-35',
                      )}
                      style={{ width: pct(d.inProgressMeanDays) }}
                    />
                  </div>
                  <span className="w-20 shrink-0" />
                </div>
              )}
              <p className="text-xs text-texto-suave">
                {d.done > 0 ? (
                  <>
                    Mediana de {inDays(d.medianDays!)} ·{' '}
                    {count(d.done, 'já saiu', 'já saíram')}
                  </>
                ) : (
                  'Nenhum saiu ainda'
                )}
                {d.inProgress > 0 && (
                  <>
                    {' '}
                    · {count(d.inProgress, 'ainda está lá', 'ainda estão lá')}, há{' '}
                    {inDays(d.inProgressMeanDays!)} em média
                  </>
                )}
              </p>
            </div>
          </li>
        ))}
      </ol>
      <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-texto-suave">
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-2.5 w-5 rounded-pilula bg-texto-suave" />
          média de quem já saiu
        </span>
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-3.5 w-0.5 rounded-pilula bg-texto" />
          mediana
        </span>
        <span className="flex items-center gap-1.5">
          <i aria-hidden className="h-1.5 w-5 rounded-pilula bg-texto-suave opacity-35" />
          há quanto tempo estão os que ainda estão lá
        </span>
      </div>
    </div>
  );
}

function DurationsTable({ data }: { data: ManagementDurations }) {
  const orDash = (n: number | null) => (n === null ? '—' : inDays(n));
  const columns: Column<Row>[] = [
    { title: 'Departamento', cell: (r) => LINE_LABEL[r.department], alignment: 'inicio' },
    { title: 'Já saíram', cell: (r) => r.done, alignment: 'fim' },
    { title: 'Média', cell: (r) => orDash(r.meanDays), alignment: 'fim' },
    { title: 'Mediana', cell: (r) => orDash(r.medianDays), alignment: 'fim' },
    { title: 'Ainda lá', cell: (r) => r.inProgress, alignment: 'fim' },
    { title: 'Há (média)', cell: (r) => orDash(r.inProgressMeanDays), alignment: 'fim' },
  ];
  return (
    <Table
      columns={columns}
      items={data.departments}
      rowKey={(r) => r.department}
      label={`Tempo por departamento nos últimos ${data.days} dias`}
      empty="Nenhum sinistro passou por um departamento no período."
    />
  );
}
